import { Component, Inject, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Auth } from '@angular/fire/auth';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { loadStripe, Stripe, StripeCardElement } from '@stripe/stripe-js';
import { environment } from 'src/app/environments/environment';

export interface PayExtraBookingDialogData {
  extraBookingPrice: number;
  extraBookingCount: number;
}

@Component({
  selector: 'app-pay-extra-booking-dialog',
  templateUrl: './pay-extra-booking-dialog.component.html',
  styleUrls: ['./pay-extra-booking-dialog.component.scss'],
})
export class PayExtraBookingDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  isProcessing = false;
  paymentError = '';
  paymentSuccess = false;

  private stripe: Stripe | null = null;
  private cardElement: StripeCardElement | null = null;
  private stripeInitialized = false;

  constructor(
    private dialogRef: MatDialogRef<PayExtraBookingDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PayExtraBookingDialogData,
    private functions: Functions,
    private auth: Auth,
    private firestore: Firestore
  ) {}

  ngOnInit(): void {}

  async ngAfterViewInit(): Promise<void> {
    // Wait for DOM to render the container
    setTimeout(() => this.initStripeCard(), 150);
  }

  ngOnDestroy(): void {
    if (this.cardElement) {
      this.cardElement.destroy();
      this.cardElement = null;
    }
  }

  private async initStripeCard(): Promise<void> {
    if (this.stripeInitialized) return;

    try {
      this.stripe = await loadStripe(environment.stripePublishableKey);
      if (!this.stripe) return;

      const elements = this.stripe.elements();
      this.cardElement = elements.create('card', {
        style: {
          base: {
            fontFamily: "'Inter', system-ui, sans-serif",
            fontSize: '15px',
            color: '#0F172A',
            '::placeholder': { color: '#94A3B8' },
          },
          invalid: { color: '#EF4444' },
        },
      });

      this.cardElement.mount('#extra-booking-card-element');
      this.stripeInitialized = true;
    } catch (err: any) {
      console.error('Stripe initialization failed:', err);
      this.paymentError = 'Failed to load card payment form.';
    }
  }

  async pay(): Promise<void> {
    if (!this.stripe || !this.cardElement || this.isProcessing) return;

    // ── Payment Enforcement Check ──
    const currentUid = this.auth.currentUser?.uid;
    if (currentUid) {
      try {
        const uSnap = await getDoc(doc(this.firestore, 'users-list', currentUid));
        if (uSnap.exists() && uSnap.data()?.['paymentsEnabled'] === false) {
          this.paymentError = 'Payment actions have been disabled for this account by administration.';
          return;
        }
      } catch (e) {
        console.error('Error verifying paymentsEnabled:', e);
      }
    }

    this.isProcessing = true;
    this.paymentError = '';

    try {
      // Step 1: Create PaymentIntent on the backend
      const createIntent = httpsCallable(this.functions, 'createExtraBookingIntent');
      const result: any = await createIntent({});

      const { clientSecret } = result.data;

      // Step 2: Confirm card payment with Stripe
      const { paymentIntent, error } = await this.stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: this.cardElement },
      });

      if (error) {
        this.paymentError = error.message || 'Payment failed.';
        this.isProcessing = false;
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        // Step 3: Tell backend to update quota
        const applyFn = httpsCallable(this.functions, 'confirmExtraBookingPayment');
        await applyFn({
          paymentIntentId: paymentIntent.id,
        });

        this.isProcessing = false;
        this.paymentSuccess = true;

        setTimeout(() => {
          this.dialogRef.close(true);
        }, 2000);
      } else {
        this.paymentError = 'Payment was not completed. Please try again.';
        this.isProcessing = false;
      }
    } catch (err: any) {
      console.error('Payment processing failed:', err);
      this.paymentError = err?.message || 'Something went wrong. Please try again.';
      this.isProcessing = false;
    }
  }

  close(): void {
    if (this.isProcessing) return;
    this.dialogRef.close(false);
  }
}
